import React, { useState } from 'react';
import { Mail, ArrowLeft, AlertCircle } from 'lucide-react';
import Button from '../../components/Button';
import Card from '../../components/Card';
import { requestPasswordReset } from '../../services/passwordResetService';
import { validateEmail } from '../../utils/emailValidation';

interface ForgotPasswordProps {
  onEmailSubmit: (email: string, message: string) => void;
  error: string | null;
  setError: (error: string | null) => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onEmailSubmit, error, setError }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const response = await requestPasswordReset(email);
      onEmailSubmit(email, response.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto p-8">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-[#E8FCFA] rounded-full">
            <Mail className="h-8 w-8 text-[#07E5D1]" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-[#1C1C1E] mb-2">Reset Your Password</h1>
        <p className="text-[#6B7280]">
          Enter your email address and we'll send you a verification code to reset your password.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[#374151] mb-2">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#07E5D1] focus:border-[#07E5D1] outline-none transition-all"
              placeholder="Enter your email address"
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <Button
            type="submit"
            fullWidth
            disabled={isLoading || !email.trim()}
            className="bg-[#07E5D1] hover:bg-[#06D4C2] text-white"
          >
            {isLoading ? (
              <>
                <span className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                Sending Reset Code...
              </>
            ) : (
              'Send Reset Code'
            )}
          </Button>

          <div className="text-center">
            <a
              href="/auth"
              className="inline-flex items-center text-sm text-[#6B7280] hover:text-[#374151] transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Login
            </a>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default ForgotPassword;
